/**
 * Business Logic Analysis Executor
 *
 * Handles the 'business-logic' and 'pipeline-business-logic' node execution.
 * Calls the ModernizeIT API Layer /businesslogic endpoint.
 *
 * API Flow (WORKER MODE):
 * 1. Verify upstream code analysis job is 'completed' (poll if needed)
 * 2. POST /businesslogic -> returns job_id with status QUEUED
 * 3. Poll GET /jobs/{job_id} -> wait until status = 'completed'
 * 4. Extract results from job.output_data
 *
 * CRITICAL: Must wait for upstream code analysis to complete before starting.
 * The COBOL paragraph data and graph nodes from code analysis are the input.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the business logic analysis node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing business logic analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Verify upstream code analysis job exists
  const caJobId = context.jobs?.code_analysis_job_id
  if (!caJobId) {
    throw new Error('Business Logic Analysis requires code analysis job ID from Code Analysis node. Run Code Analysis first.')
  }

  // 2. Verify upstream code analysis job is completed (poll if needed)
  console.log(`[BusinessLogicExecutor] Verifying upstream code analysis job ${caJobId} is completed...`)
  onProgress(2, 'Waiting for Code Analysis to complete...')

  try {
    await pollJobUntilComplete(caJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        const mappedProgress = 2 + (progress * 0.06)
        onProgress(mappedProgress, `Waiting for Code Analysis: ${message}`)
      },
      signal
    })
    console.log(`[BusinessLogicExecutor] Upstream code analysis job ${caJobId} is completed`)
  } catch (error) {
    throw new Error(`Upstream Code Analysis job failed: ${error.message}`)
  }

  onProgress(8, 'Code Analysis complete, starting business logic analysis...')

  // 3. Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id,
    code_analysis_job_id: caJobId,
    workflow_run_id: context.workflow_run_id || null,
  }

  onProgress(10, 'Submitting business logic analysis job...')

  // 4. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/businesslogic`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Business logic analysis failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  console.log(`[BusinessLogicExecutor] Job created: ${jobId}, polling until completed...`)
  onProgress(15, `Job started: ${jobId}`)

  // 5. Poll for completion using generic jobs endpoint
  const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
    maxWaitSeconds: 600,
    pollInterval: 1000,
    onProgress: (progress, message) => {
      const mappedProgress = 15 + (progress * 0.80)
      onProgress(mappedProgress, message)
    },
    signal
  })

  console.log('[BusinessLogicExecutor] Job completed, extracting results from output_data')
  onProgress(95, 'Extracting results...')

  // 6. Extract results from job.output_data (worker mode)
  const outputData = completedJob.output_data
  if (!outputData) {
    throw new Error('Job completed but has no output_data')
  }

  onProgress(100, 'Business logic analysis complete')

  // 7. Return formatted result
  return formatResult(jobId, outputData, context.jobs)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data, contextJobs = {}) {
  console.log('[BusinessLogicExecutor] formatResult called with contextJobs:', contextJobs)

  const resultJobs = {
    ...contextJobs,  // Preserve job chain from upstream nodes
    business_logic_job_id: jobId,
  }

  console.log('[BusinessLogicExecutor] Returning jobs:', resultJobs)

  return {
    // Job tracking — preserve upstream job IDs for downstream nodes
    jobs: resultJobs,

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Summary statistics
    summary: data.summary || {},

    // Graph loading results
    graph: data.graph || null,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
